import { Theme } from '../../../../types';
import KlydoColorStackItem from './KlydoColorStackItem';
import DSIconButton from '../../../../design-system/DSIconButton';
import AddIcon from '@mui/icons-material/Add';
import DSBox from '../../../../design-system/DSBox';
import KlydoColorPickerModal from './KlydoColorPickerModal';
import DSPaper from '../../../../design-system/Paper/DSPaper';
import DSCircularProgress from '../../../../design-system/DSCircularProgress';
import DSModal from '../../../../design-system/DSModal';
import { useTheme } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import DSTooltip from '../../../../design-system/DSTooltip';
import { areThemesEqual } from '../../../../utils/themeUtils';

const MAX_PALETTES = 10;

type KlydoColorStackProps = {
  themes?: Theme[];
  setTheme: (theme: Theme[]) => void;
  onChangeTheme: (theme: Theme | undefined) => void;
  onChangeColor: (key: string, color: string) => boolean;
  isLoadingThemes?: boolean;
  addThemeButtonDisabled: boolean;
  handleSaveTheme: (theme: Theme) => void;
  currentTheme?: Theme;
};

const KlydoColorStack = ({
  themes,
  setTheme,
  onChangeTheme,
  onChangeColor,
  isLoadingThemes,
  addThemeButtonDisabled,
  handleSaveTheme,
  currentTheme,
}: KlydoColorStackProps) => {
  const { spacing, palette } = useTheme();

  const [openColorPicker, setOpenColorPicker] = useState<boolean>(false);
  const [selectedThemeIndex, setSelectedThemeIndex] = useState<
    number | undefined
  >();
  const editingThemeRef = useRef<Theme | undefined>(undefined);

  const handleThemeChange = useCallback((theme: Theme) => {
    editingThemeRef.current = theme;
  }, []);

  const handleModalClose = useCallback(() => {
    const updatedTheme = editingThemeRef.current;
    if (updatedTheme && Object.values(updatedTheme).some((v) => v !== '')) {
      if (selectedThemeIndex !== undefined && themes) {
        const updatedThemes = themes.map((theme, i) =>
          i === selectedThemeIndex ? updatedTheme : theme,
        );
        setTheme(updatedThemes);
      } else {
        setTheme([...(themes || []), updatedTheme]);
      }
      onChangeTheme(updatedTheme);
      handleSaveTheme(updatedTheme);
    }
    setOpenColorPicker(false);
    setSelectedThemeIndex(undefined);
    editingThemeRef.current = undefined;
  }, [selectedThemeIndex, themes, setTheme, onChangeTheme, handleSaveTheme]);

  const handleColorClick = useCallback(
    (index: number) => {
      if (themes === undefined) return;
      const selectedTheme = themes[index];
      onChangeTheme(selectedTheme);
      handleSaveTheme(selectedTheme);
    },
    [onChangeTheme, themes, handleSaveTheme],
  );

  const handleDeletePalette = useCallback(() => {
    if (!themes || selectedThemeIndex === undefined) return;

    const newThemes = themes.filter((_, i) => i !== selectedThemeIndex);
    setTheme(newThemes);

    if (newThemes.length > 0) {
      handleColorClick(0);
    } else {
      onChangeTheme(undefined);
    }

    setOpenColorPicker(false);
    setSelectedThemeIndex(undefined);
  }, [
    themes,
    selectedThemeIndex,
    setTheme,
    handleColorClick,
    onChangeTheme,
    setOpenColorPicker,
    setSelectedThemeIndex,
  ]);

  if (isLoadingThemes) {
    return (
      <DSPaper
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignSelf: 'stretch',
          width: '100%',
          height: spacing(25),
          paddingX: spacing(7),
          paddingY: spacing(3),
          gap: spacing(2),
          backgroundColor: palette.white[9],
        }}
      >
        <DSCircularProgress />
      </DSPaper>
    );
  }

  return (
    <DSPaper
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignSelf: 'stretch',
        width: '100%',
        padding: spacing(2),
        gap: spacing(2),
        backgroundColor: palette.white[9],
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: spacing(1),
        }}
      >
        {themes?.map((colorOption: Theme, index: number) => (
          <DSBox
            key={index}
            sx={{ display: 'flex', width: '100%' }}
            onClick={() => handleColorClick(index)}
          >
            <KlydoColorStackItem
              isSelected={
                currentTheme ? areThemesEqual(colorOption, currentTheme) : false
              }
              theme={colorOption}
              onClickEdit={() => {
                setSelectedThemeIndex(index);
                onChangeTheme(themes[index]);
                setOpenColorPicker(true);
              }}
            />
          </DSBox>
        ))}

        <DSTooltip
          title={
            themes && themes.length >= MAX_PALETTES
              ? `You've reached the maximum of ${MAX_PALETTES} color palettes`
              : ''
          }
          placement="top"
        >
          <DSBox sx={{ width: '100%' }}>
            <DSIconButton
              onClick={() => {
                setOpenColorPicker(true);
                setSelectedThemeIndex(undefined);
              }}
              disabled={
                addThemeButtonDisabled ||
                (themes && themes.length >= MAX_PALETTES)
              }
              sx={{
                fontSize: '24px',
                width: '100%',
                height: '47px',
                padding: 'var(--2, 16px) var(--3, 24px)',
                borderRadius: 'var(--3, 24px)',
                border: `3px solid var(--action-active, ${palette.white[56]})`,
              }}
            >
              <AddIcon />
            </DSIconButton>
          </DSBox>
        </DSTooltip>
      </DSBox>
      <DSModal
        open={openColorPicker}
        onClose={handleModalClose}
        slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }}
      >
        <KlydoColorPickerModal
          onThemeChange={handleThemeChange}
          onDelete={
            selectedThemeIndex !== undefined ? handleDeletePalette : undefined
          }
          onChangeColor={onChangeColor}
          selectedThemeIndex={selectedThemeIndex}
          theme={
            selectedThemeIndex !== undefined
              ? themes?.[selectedThemeIndex]
              : undefined
          }
        />
      </DSModal>
    </DSPaper>
  );
};
export default KlydoColorStack;
